'use client'

import { FileText } from 'react-feather'
import { useTranslations } from '@/lib/next-shim'
import { DeleteOrderDialog, OrderCard, useDeleteOrder, type Order } from '@/entities/order'

import { OrdersFilterTabs, type OrdersFilterTabValue } from './OrdersFilterTabs'

type OrdersListProps = {
  orders: Order[]
  tab: OrdersFilterTabValue
  error?: string
}

export function OrdersList({ orders, tab, error }: OrdersListProps) {
  const t = useTranslations()
  const { orderToDelete, requestDelete, cancelDelete, confirmDelete } = useDeleteOrder()

  if (error) {
    return <div className="rounded-lg bg-red-50 p-4 text-red-600">{error}</div>
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2.5">
          <h2 className="section-title">{t('account.sections.Orders.title')}</h2>
          {!!orders.length && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary">{orders.length}</span>}
        </div>
        <OrdersFilterTabs defaultTab={tab} />
      </div>

      <section>
        {orders.length ? (
          <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2">
            {orders.map((order) => (
              <OrderCard key={order._id} order={order} onDelete={requestDelete} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <FileText size={22} aria-hidden="true" />
            </span>
            <p className="text-sm font-semibold text-gray-900">{t('account.sections.Orders.empty')}</p>
            <p className="max-w-xs text-sm text-gray-500">{t('account.sections.Orders.emptyHint')}</p>
          </div>
        )}
      </section>

      {orderToDelete && <DeleteOrderDialog onCancel={cancelDelete} onConfirm={confirmDelete} />}
    </div>
  )
}
